<script setup lang="ts">

const props = withDefaults(defineProps<{
  newsletter?: 'B2B' | 'default'
  buttonText?: string
  oneClick?: boolean
  placeholderText?: string
  inputLabel?: string
  errorText?: string
  successText?: string
  showIcon?: boolean
}>(), {
  newsletter: 'default',
  buttonText: 'Subscribe',
  oneClick: false,
  placeholderText: 'Your E-mail',
  inputLabel: 'E-mail *',
  errorText: 'This is a required field!',
  successText: 'You are subscribed. Thanks!',
  showIcon: true,
})

const emit = defineEmits<{
  'update:successfullySubmitted': [value: boolean]
}>()

const { $api } = useNuxtApp();
const { user, isAuthenticated } = useAuthUser();
const { pushGTMValues } = useWadGTM();

const form = reactive({
  email: '',
  terms: false,
})

const errors = reactive({
  email: null,
  terms: null,
});

const successfullySubmitted = ref(false);
const submitting = ref(false);

const legalText = computed(() => {
  if(props.newsletter === 'B2B') {
    return 'I agree to receive HR and recruiting-related news, offers and the latest announcements from WeAreDevelopers.';
  }
  return 'I agree to receive updates regarding WeAreDevelopers Live, developer news, discounts and the latest announcements.';
})

function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

function validate(): boolean {
  errors.email = null;
  errors.terms = null;

  if(!props.oneClick) {
    if(!form.email) {
      errors.email = 'Enter a valid email address';
    } else if(!validateEmail(form.email)) {
      errors.email = 'Enter a valid email address';
    }

    if(!form.terms) {
      errors.terms = props.errorText;
    }
  }

  return !errors.email && !errors.terms
}

async function submitForm() {
  if(props.oneClick && isAuthenticated.value && user.value?.email) {
    form.email = user.value.email;
    form.terms = true;
  }

  if(!validate() || submitting.value) {
    return;
  }

  submitting.value = true;

  try {
    const endpoint = props.newsletter === 'B2B' ? '/v2/integration/hubspot/form/b2b-newsletter' : '/v2/integration/hubspot/form/newsletter';

    await $api(endpoint, {
      method: 'POST',
      body: form,
    });

    successfullySubmitted.value = true;
    emit('update:successfullySubmitted', true);

    // Track GTM event
    pushGTMValues('newsletter_subscription', props.newsletter === 'B2B' ? 'b2b_newsletter' : 'dev_digest');
  } catch (e: any) {
    if(e?.response?._data?.fields?.email) {
      errors.email = e.response._data.fields.email;
    }
  }

  submitting.value = false;
}
</script>

<template>
  <div class="wad4-newsletter-form">
    <template v-if="!successfullySubmitted">
      <template v-if="!oneClick">
        <FormInputField v-model="form.email" :invalid="errors.email" :placeholder="placeholderText" label="E-mail" required />

        <CommonBaseCheckbox v-model="form.terms" :error="errors.terms">{{ legalText }}</CommonBaseCheckbox>
      </template>

      <button class="wad4-button-primary" :disabled="submitting" @click="submitForm">
        {{ buttonText }}
      </button>
    </template>

    <div v-else class="wad4-newsletter-form__success">
      <svg v-if="showIcon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="#20A977"/>
      </svg>
      <span>{{ successText }}</span>
    </div>
  </div>
</template>

<style lang="scss">
.wad4-newsletter-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;

  .wad4-button-primary {
    align-self: flex-start;
  }

  &__success {
    display: flex;
    align-items: center;
    gap: $wad4-spacing-xs;
    @include text-size-mixin('lg');
    color: $wad4-c-text-text-secondary;

    svg {
      flex-shrink: 0;
    }
  }

  & .wad4-base-checkbox__content {
    color: $wad4-c-text-text-secondary;
  }
}
</style>
